export type DisclosureSegment =
  | { type: 'text'; content: string }
  | { type: 'link'; content: string; url: string };

export type DisclosureParagraph = DisclosureSegment[];

const PARAGRAPH_SPLIT_PATTERN = /<\/p\s*>\s*<p[^>]*>|<br\s*\/?>|\n{2,}/gi;
const ANCHOR_PATTERN = /<a\b[^>]*?\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
const TAG_STRIP_PATTERN = /<\/?[a-z][^>]*>/gi;

function decodeEntities(input: string): string {
  return input
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

function collapseWhitespace(input: string): string {
  return input.replace(/\s+/g, ' ');
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) return true;
  return /^(https?:|mailto:|tel:)/i.test(trimmed);
}

function stripTagsPreservingSpace(input: string): string {
  return collapseWhitespace(decodeEntities(input.replace(TAG_STRIP_PATTERN, '')));
}

function parseParagraphSegments(paragraphHtml: string): DisclosureSegment[] {
  const segments: DisclosureSegment[] = [];
  let cursor = 0;
  const regex = new RegExp(ANCHOR_PATTERN.source, 'gi');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(paragraphHtml)) !== null) {
    const [full, rawUrl, rawText] = match;
    const before = paragraphHtml.slice(cursor, match.index);
    const beforeText = stripTagsPreservingSpace(before);
    if (beforeText) segments.push({ type: 'text', content: beforeText });

    const linkText = stripTagsPreservingSpace(rawText).trim();
    if (linkText) {
      const decodedUrl = decodeEntities(rawUrl).trim();
      if (isSafeUrl(decodedUrl)) {
        segments.push({ type: 'link', content: linkText, url: decodedUrl });
      } else {
        segments.push({ type: 'text', content: linkText });
      }
    }
    cursor = match.index + full.length;
  }

  const tail = stripTagsPreservingSpace(paragraphHtml.slice(cursor));
  if (tail) segments.push({ type: 'text', content: tail });

  if (segments.length > 0) {
    const first = segments[0];
    if (first.type === 'text') first.content = first.content.replace(/^\s+/, '');
    const last = segments[segments.length - 1];
    if (last.type === 'text') last.content = last.content.replace(/\s+$/, '');
  }

  return segments.filter(seg => seg.content.length > 0);
}

export function parseCustomDisclosures(html: string | null | undefined): DisclosureParagraph[] {
  if (!html) return [];
  const normalized = html.replace(/\r\n?/g, '\n').trim();
  if (!normalized) return [];

  const rawParagraphs = normalized
    .replace(/^<p[^>]*>/i, '')
    .replace(/<\/p\s*>\s*$/i, '')
    .split(PARAGRAPH_SPLIT_PATTERN);

  return rawParagraphs
    .map(parseParagraphSegments)
    .filter(segments => segments.length > 0);
}

export function hasCustomDisclosuresContent(html: string | null | undefined): boolean {
  return parseCustomDisclosures(html).length > 0;
}

export function getCustomDisclosuresTitle(organizationName: string | null | undefined): string {
  const trimmed = organizationName?.trim();
  return trimmed ? `${trimmed} Disclosures` : 'Advisor Disclosures';
}
